/**
 * Backorder React Query Hooks
 *
 * Provides data fetching and mutation hooks for backorders
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import type {
  Backorder,
  BackorderKpis,
  Priority,
  FulfillBackorderDto,
} from '@lognai/plugin-orders';
import {
  listBackorders,
  getBackordersByOrder,
  getBackordersByProduct,
  getBackorder,
  fulfillBackorder,
  cancelBackorder,
  updateBackorderPriority,
  getBackorderKpis,
  type BackorderFilterParams,
  type PaginatedBackorderList,
} from '../api/backorders';
import { backorderKeys, salesOrderKeys } from './query-keys';

/**
 * Hook to fetch paginated backorders
 */
export const useBackorders = (
  params?: BackorderFilterParams,
  options?: Omit<UseQueryOptions<PaginatedBackorderList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: backorderKeys.list(params),
    queryFn: () => listBackorders(params),
    ...options,
  });
};

/**
 * Hook to fetch backorders by order ID
 */
export const useBackordersByOrder = (
  orderId: string,
  params?: BackorderFilterParams,
  options?: Omit<UseQueryOptions<PaginatedBackorderList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: backorderKeys.byOrder(orderId, params),
    queryFn: () => getBackordersByOrder(orderId, params),
    enabled: !!orderId,
    ...options,
  });
};

/**
 * Hook to fetch backorders by product ID
 */
export const useBackordersByProduct = (
  productId: string,
  params?: BackorderFilterParams,
  options?: Omit<UseQueryOptions<PaginatedBackorderList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: backorderKeys.byProduct(productId, params),
    queryFn: () => getBackordersByProduct(productId, params),
    enabled: !!productId,
    ...options,
  });
};

/**
 * Hook to fetch a single backorder
 */
export const useBackorder = (
  id: string,
  options?: Omit<UseQueryOptions<Backorder>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: backorderKeys.detail(id),
    queryFn: () => getBackorder(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Hook to fetch backorder KPIs
 */
export const useBackorderKpis = (
  options?: Omit<UseQueryOptions<BackorderKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: backorderKeys.kpis(),
    queryFn: getBackorderKpis,
    ...options,
  });
};

/**
 * Hook to fulfill a backorder
 */
export const useFulfillBackorder = (
  options?: Omit<
    UseMutationOptions<Backorder, Error, { id: string; dto: FulfillBackorderDto }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => fulfillBackorder(id, dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: backorderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: backorderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: backorderKeys.kpis() });
      // Also invalidate related sales order
      if (data.orderId) {
        queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.orderId) });
        queryClient.invalidateQueries({ queryKey: salesOrderKeys.stockStatus(data.orderId) });
      }
    },
    ...options,
  });
};

/**
 * Hook to cancel a backorder
 */
export const useCancelBackorder = (
  options?: Omit<
    UseMutationOptions<Backorder, Error, { id: string; reason: string }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => cancelBackorder(id, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: backorderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: backorderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: backorderKeys.kpis() });
      // Also invalidate related sales order
      if (data.orderId) {
        queryClient.invalidateQueries({ queryKey: salesOrderKeys.detail(data.orderId) });
      }
    },
    ...options,
  });
};

/**
 * Hook to update backorder priority
 */
export const useUpdateBackorderPriority = (
  options?: Omit<
    UseMutationOptions<Backorder, Error, { id: string; priority: Priority; reason?: string }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, priority, reason }) => updateBackorderPriority(id, priority, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: backorderKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: backorderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: backorderKeys.kpis() });
    },
    ...options,
  });
};
